import {
	Box,
	Button,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { useEffect, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
	formatedDateTime,
	parseDate,
} from "../../commons/constants/helpers";
import "../../styles/Table.css";

export interface Column {
	field: string;
	headerName: string;
	flex?: number;
	type?: "string" | "number" | "date" | "dateTime";
}

const isDateColumn = (column: Column) =>
	column.type === "date" || column.type === "dateTime";

// Columnas que sólo contienen elementos de React: no tiene sentido ordenarlas
// ni filtrarlas y además rompen los operadores del DataGrid.
const NON_SORTABLE_FIELDS = ["action", "checked"];

export interface IAction {
	label: string;
	action: (item: any) => void;
	visible?: false;
}

interface TableProps {
	columns: Column[];
	rows: GridRowsProp;
	pageSize?: number;
	headerName: string;
	actions?: IAction[];
	loading?: boolean;
	onPageChange?: any;
	buttonRef?: string;
	buttonRefClick?: any;
	/** Campo por el que se ordena la tabla al abrirla. */
	sortField?: string;
	sortDirection?: "asc" | "desc";
}

export const CustomTable = ({
	columns,
	rows,
	pageSize = 5,
	actions,
	loading = false,
	onPageChange = () => {},
	buttonRef,
	buttonRefClick,
	sortField,
	sortDirection = "desc",
}: TableProps) => {
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [openMenuId, setOpenMenuId] = useState<null | string>(null);
	const [paginationModel, setPaginationModel] = useState({
		pageSize: pageSize,
		page: 0,
	});

	useEffect(() => {
		onPageChange(paginationModel.page);
	}, [paginationModel.page]);

	const handleMenuClick = (
		event: React.MouseEvent<HTMLButtonElement>,
		id: string
	) => {
		setMenuAnchorEl(event.currentTarget);
		setOpenMenuId(id);
	};

	const handleClose = () => {
		setMenuAnchorEl(null);
		setOpenMenuId(null);
	};

	const columnsWithRenderCell: GridColDef[] = columns.map((column) => {
		// Las columnas de fecha se entregan al DataGrid como objetos Date para
		// que el ordenamiento y los filtros por fecha funcionen; el texto
		// visible se arma en renderCell.
		if (isDateColumn(column)) {
			return {
				...column,
				headerClassName: "columnHeader",
				flex: 1,
				valueGetter: (params) => parseDate(params.row[column.field]),
				// params.value ya es Date|null; cuando parseDate no supo leer el
				// valor mostramos el original en crudo en vez de una celda vacía.
				renderCell: (params) => (
					<span className={"rowItemText"}>
						{params.value
							? formatedDateTime(params.value)
							: params.row[column.field] ?? ""}
					</span>
				),
			};
		}

		const isRenderedColumn = NON_SORTABLE_FIELDS.includes(column.field);

		return {
			...column,
			headerClassName: "columnHeader",
			flex: 1,
			sortable: !isRenderedColumn,
			filterable: !isRenderedColumn,
			renderCell: (params) => (
				<span className={"rowItemText"}>{params.value}</span>
			),
		};
	});

	const rowsWithActions = actions
		? rows.map((item, index) => ({
				...item,
				action: (
					<Box>
						<IconButton
							onClick={(e) =>
								handleMenuClick(e, item.id.toString())
							}
							color="primary"
						>
							<MoreVertIcon />
						</IconButton>
						<Menu
							anchorEl={menuAnchorEl}
							open={openMenuId === item.id.toString()}
							onClose={handleClose}
						>
							{actions.map((option, index) => (
								<MenuItem
									disabled={loading}
									onClick={() => {
										option.action(item);
										handleClose();
									}}
									key={index}
								>
									{option.label}
								</MenuItem>
							))}
						</Menu>
					</Box>
				),
		  }))
		: rows;

	return (
		<Grid
			container
			style={{
				minHeight: "600px",
				height: "auto",
				marginTop: "30px",
			}}
		>
			{buttonRef && (
				<Box sx={{ position: "absolute", right: "30px", top: "110px" }}>
					<Button variant="contained" onClick={buttonRefClick}>
						{buttonRef}
					</Button>
				</Box>
			)}
			<DataGrid
				className={"table"}
				localeText={
					esES.components.MuiDataGrid.defaultProps.localeText
				}
				rows={rowsWithActions}
				columns={columnsWithRenderCell}
				pageSizeOptions={[25]}
				pagination
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				loading={loading}
				autoPageSize
				initialState={
					sortField
						? {
								sorting: {
									sortModel: [
										{
											field: sortField,
											sort: sortDirection,
										},
									],
								},
						  }
						: undefined
				}
			/>
		</Grid>
	);
};
